import { supabase } from "@/infrastructure/supabase/client";
import type {
  FeatureProductCoverage,
  FeatureProductRow,
} from "@/infrastructure/supabase/database.types";

export interface UpsertFeatureProductInput {
  product_id: string;
  feature_id: string;
  coverage: FeatureProductCoverage;
  notes_md: string | null;
  effort_estimate_weeks: number | null;
}

// Supabase REST defaults to a 1000-row hard cap per request. As the
// product × feature matrix grows (currently >1300 rows), naive `select("*")`
// silently truncates — irrelevant rows added by later product audits drop
// off the bottom and the matrix renders "no row" instead of the actual
// classification. Page through using `.range()` until a short page comes back.
const PAGE_SIZE = 1000;

export const featureProductsRepository = {
  async list(): Promise<FeatureProductRow[]> {
    const all: FeatureProductRow[] = [];
    let from = 0;
    for (;;) {
      const { data, error } = await supabase
        .from("feature_products")
        .select("*")
        .range(from, from + PAGE_SIZE - 1);
      if (error) throw error;
      const page = data ?? [];
      all.push(...page);
      if (page.length < PAGE_SIZE) break;
      from += PAGE_SIZE;
    }
    return all;
  },

  async listByProduct(productId: string): Promise<FeatureProductRow[]> {
    const { data, error } = await supabase
      .from("feature_products")
      .select("*")
      .eq("product_id", productId);
    if (error) throw error;
    return data ?? [];
  },

  async upsert(input: UpsertFeatureProductInput): Promise<FeatureProductRow> {
    const { data, error } = await supabase
      .from("feature_products")
      .upsert(input, { onConflict: "product_id,feature_id" })
      .select("*")
      .single();
    if (error) throw error;
    return data;
  },

  async remove(productId: string, featureId: string): Promise<void> {
    const { error } = await supabase
      .from("feature_products")
      .delete()
      .eq("product_id", productId)
      .eq("feature_id", featureId);
    if (error) throw error;
  },
};
